/*
  Подтверждение удаления счетов - решение владельца 16.10.2026.

  Окно одно на реестр и на карточку счёта. Открывшись, оно спрашивает сервер,
  что из выбранного удалится, а что нет и почему: правило «по счёту уже идёт
  дело - не удалять» живёт на сервере одно, и окно не угадывает его по стадии
  в строке. Само удаление сервер считает заново - между окном и нажатием по
  счёту могли подать заявление.

  Удалять нечего - в окне одна кнопка «Понятно»: красная кнопка рядом с
  «Отмена» при пустом действии предлагала бы нажать то, что ничего не сделает.
*/

import { useEffect, useState } from 'react'

import { api } from '../api/client.js'
import { accounts } from '../api/format.js'
import { Loading, Notice } from './Common.jsx'
import Modal from './Modal.jsx'

const SHOWN = 30
/* Сколько номеров называть списком. Двести номеров через запятую не читает
   никто, а первые тридцать и «и ещё сто семьдесят» отвечают на вопрос «те ли». */

const LOST =
  'Вместе со счётом удалятся его должники, начисления, расчёты, история, обращения и ' +
  'сведения из домовой книги. Вернуть их нельзя - только загрузить заново. В журнале ' +
  'действий останется запись: кто, когда и какой счёт удалил.'

function numbers(rows) {
  const head = rows.slice(0, SHOWN).map((row) => `№ ${row.number}`)
  const rest = rows.length - head.length
  return rest > 0 ? `${head.join(', ')} и ещё ${accounts(rest)}` : head.join(', ')
}

function Refused({ refused }) {
  if (refused.length === 0) return null
  return (
    <>
      <p>
        <b>Не удалятся - по ним уже идёт дело ({refused.length}):</b>
      </p>
      <ul className="list-plain">
        {refused.slice(0, SHOWN).map((row) => (
          <li key={row.account_id}>
            № {row.number} - {row.reasons.join(', ')}
          </li>
        ))}
      </ul>
      {refused.length > SHOWN ? (
        <p className="muted">и ещё {accounts(refused.length - SHOWN)} с такими же следами дела.</p>
      ) : null}
      <p className="muted">
        Такой счёт выводится из работы стадией «исключён из взыскания»: след дела при этом
        сохраняется.
      </p>
    </>
  )
}

export default function AccountsRemoveDialog({ chosen, onClose, onRemoved }) {
  const [review, setReview] = useState(null)
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    let current = true
    api
      .removalReview(chosen.map((row) => row.account_id))
      .then((result) => current && setReview(result))
      .catch((failure) => current && setError(failure))
    return () => {
      current = false
    }
  }, [chosen])

  async function remove() {
    setBusy(true)
    setError(null)
    try {
      const result = await api.removeAccounts(review.removable.map((row) => row.account_id))
      if (result.removable.length === 0) {
        // Между окном и нажатием по счёту пришёл платёж или подача: удалять
        // нечего, и окно показывает новую причину, а не закрывается молча.
        setReview(result)
        setBusy(false)
        return
      }
      onRemoved(result)
    } catch (failure) {
      setError(failure)
      setBusy(false)
    }
  }

  const removable = review?.removable ?? []
  const title = !review
    ? 'Удаление счетов'
    : removable.length > 0
      ? `Удалить ${accounts(removable.length)}?`
      : 'Удалить нельзя'

  return (
    <Modal title={title} onClose={onClose} wide busy={busy}>
      <div className="modal__text">
        {!review && !error ? <Loading what="Проверяем, какие счета можно удалить" /> : null}
        {removable.length > 0 ? (
          <>
            <p>{LOST}</p>
            <p>
              <b>Удалятся:</b> {numbers(removable)}
            </p>
            <p className="muted">
              Если счета приходят обменом с 1С, ночной обмен заведёт их снова - уберите их и в 1С.
            </p>
          </>
        ) : null}
        {review ? <Refused refused={review.refused} /> : null}
        {review?.missing ? (
          <p className="muted">Ещё {accounts(review.missing)} уже нет - их удалили раньше.</p>
        ) : null}
      </div>
      <Notice error={error} />
      <div className="row row--tight">
        {removable.length > 0 ? (
          <button type="button" className="button button--danger" onClick={remove} disabled={busy}>
            Удалить {accounts(removable.length)}
          </button>
        ) : null}
        <button type="button" className="button button--quiet" onClick={onClose} disabled={busy}>
          {review && removable.length === 0 ? 'Понятно' : 'Отмена'}
        </button>
      </div>
    </Modal>
  )
}
